// Order confirmation view
// This the confirmation modal view which shows a summary of the order and
// the button to confirm the order (process payment and send it to the store)
import i18n from 'i18next';

const { UI } = Mobi2Go;
const { Order } = Mobi2Go;

/**-----------------------------------------------------------------------------
 * Progress Errors
 */
UI.View({
    name: 'progress_errors',
    model: Order,
    model_listeners: {
        updateProgressPhase: 'updateProgressPhase',
        updateProgressError: 'alertProgressError',
        updatePaymentProcessorError: 'alertPaymentError',
    },

    // New payment error handling code
    alertPaymentError(error) {
        let alert = {};

        switch (error.toLowerCase()) {
            case 'insufficient funds':
                alert = {
                    title: i18n.t(
                        'order.progress-errors:insufficient_funds_title'
                    ),
                    body: i18n.t(
                        'order.progress-errors:insufficient_funds_body'
                    ),
                };
                break;

            case 'invalid card number':
                alert = {
                    title: i18n.t(
                        'order.progress-errors:invalid_card_number_title'
                    ),
                    body: i18n.t(
                        'order.progress-errors:invalid_card_number_body'
                    ),
                };
                break;

            case 'invalid expiry':
                alert = {
                    title: i18n.t('order.progress-errors:invalid_expiry_title'),
                    body: i18n.t('order.progress-errors:invalid_expiry_body'),
                };
                break;

            case 'card expired':
                alert = {
                    title: i18n.t('order.progress-errors:card_expired_title'),
                    body: i18n.t('order.progress-errors:card_expired_body'),
                };
                break;

            case 'payment error':
                alert = {
                    title: i18n.t('order.progress-errors:payment_error_title'),
                    body: i18n.t('order.progress-errors:payment_error_body'),
                };
                break;

            case 'user rejected the payment':
                return;

            default:
                alert = {
                    title: i18n.t('order.progress-errors:payment_error_title'),
                    body: i18n.t('order.progress-errors:payment_error_body'),
                };
        }

        UI.centeredAlert(alert);
    },

    alertProgressError(error) {
        let alert = {};

        if (localStorage) {
            try {
                const last_progress_error = localStorage.getItem(
                    'Mobi2GoLastProgressError'
                );
                const current_progress_error = error.message + error.timestamp;
                if (last_progress_error === current_progress_error) {
                    return;
                }
                localStorage.setItem(
                    'Mobi2GoLastProgressError',
                    current_progress_error
                );
            } catch (e) {}
        }

        switch (error.message.toLowerCase()) {
            // Payment error messages
            // New versions of payment processors use the payment.transaction.last_error
            // property instead of the progress.status property to handle errors.
            // Once all payment processors have been updated with Nils' new code
            // we can delete alerts below.
            case 'insufficient funds':
                alert = {
                    title: i18n.t(
                        'order.progress-errors:insufficient_funds_title'
                    ),
                    body: i18n.t(
                        'order.progress-errors:insufficient_funds_body'
                    ),
                };
                break;

            case 'invalid card number':
                alert = {
                    title: i18n.t(
                        'order.progress-errors:invalid_card_number_title'
                    ),
                    body: i18n.t(
                        'order.progress-errors:invalid_card_number_body'
                    ),
                };
                break;

            case 'invalid expiry':
                alert = {
                    title: i18n.t('order.progress-errors:invalid_expiry_title'),
                    body: i18n.t('order.progress-errors:invalid_expiry_body'),
                };
                break;

            case 'card expired':
                alert = {
                    title: i18n.t('order.progress-errors:card_expired_title'),
                    body: i18n.t('order.progress-errors:card_expired_body'),
                };
                break;

            case 'payment error':
                alert = {
                    title: i18n.t('order.progress-errors:payment_error_title'),
                    body: i18n.t('order.progress-errors:payment_error_body'),
                };
                break;

            case 'user rejected the payment':
                return;

            case "customer's password is required for tokenised payment":
                alert = {
                    title: i18n.t(
                        'order.progress-errors:token_password_required_title'
                    ),
                    body: i18n.t(
                        'order.progress-errors:token_password_required_body'
                    ),
                };
                break;

            case 'dispatcher offline':
                handleDispatcherFailure();
                return;

            default:
                // "dispatch failed" error
                handleDispatcherFailure(true);

                return;
        }

        UI.centeredAlert(alert);
    },

    updateProgressPhase(phase) {
        switch (phase) {
            case 'confirmed':
                cancelAbandonmentCountdown();
                break;
            default:
                break;
        }
    },
});

/**-----------------------------------------------------------------------------
 */
function handleDispatcherFailure(refund_message_required) {
    Order.log('dispatcher failure prompt shown');
    startAbandonmentCountdown(refund_message_required);
    const order_id = Order.getId();
    UI.centeredAlert({
        title: i18n.t('order.progress-errors:dispatcher_failure_title'),
        body: i18n.t('order.progress-errors:dispatcher_failure_body', {
            order_id,
        }),
        primary_button: i18n.t(
            'order.progress-errors:dispatcher_failure_primary_button'
        ),
        secondary_button: i18n.t(
            'order.progress-errors:dispatcher_failure_secondary_button'
        ),
        onPrimaryButtonClick() {
            Order.log('dispatcher failure prompt retry clicked');
            cancelAbandonmentCountdown();
            Order.confirm();
        },
        onSecondaryButtonClick() {
            Order.log('dispatcher failure prompt cancel clicked');
            cancelAbandonmentCountdown();
            doAbandonment(refund_message_required);
        },
    });
}

function cancellationWithRefundAlert() {
    const { phone_number } = Order.getLocation().address;

    UI.centeredAlert({
        title: i18n.t('order.progress-errors:cancellation_w_refund_title'),
        body: i18n.t('order.progress-errors:cancellation_w_refund_body', {
            phone_number,
        }),
    });
}

function cancellationAlert() {
    const { phone_number } = Order.getLocation().address;

    UI.centeredAlert({
        title: i18n.t('order.progress-errors:cancellation_title'),
        body: i18n.t('order.progress-errors:cancellation_body', {
            phone_number,
        }),
    });
}

function doAbandonment(refund_message_required) {
    Order.abandon();
    UI.trigger('hideConfirmation');

    if (Order.getPaymentMethod() === 'online' && refund_message_required) {
        cancellationWithRefundAlert();
    } else {
        cancellationAlert();
    }
}

function startAbandonmentCountdown(refund_message_required) {
    cancelAbandonmentCountdown();
    startAbandonmentCountdown.timeout = setTimeout(function() {
        Order.log('dispatcher failure prompt timed-out and auto-cancelled');
        doAbandonment(refund_message_required);
    }, 5 * 60 * 1000); // 5 Minutes
}

function cancelAbandonmentCountdown() {
    clearTimeout(startAbandonmentCountdown.timeout);
}
